/**
 * Histórico de avaliações do utilizador. Mostra lista + radar comparativo
 * das últimas 2 avaliações escolhidas.
 */
import { useEffect, useMemo, useState } from "react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { History, Trash2, Eye, Loader2 } from "lucide-react";
import { myApi } from "../../api";
import RodaEvolucao from "./RodaEvolucao";

const isMobileWidth = () => typeof window !== "undefined" && window.innerWidth < 640;

export default function RodaHistorico({ onPick, onError }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]); // até 2 IDs para comparação

  const load = async () => {
    setLoading(true);
    try {
      const list = await myApi.coachRodaHistory();
      setItems(list);
      // Selecciona automaticamente as 2 mais recentes.
      setSelected(list.slice(0, 2).map((d) => d.id));
    } catch (e) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setSelected((sel) => {
      if (sel.includes(id)) return sel.filter((x) => x !== id);
      if (sel.length >= 2) return [sel[1], id]; // mantém max 2 (FIFO)
      return [...sel, id];
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar esta avaliação? Não é possível recuperar."))
      return;
    try {
      await myApi.coachRodaDelete(id);
      setItems((it) => it.filter((x) => x.id !== id));
      setSelected((sel) => sel.filter((x) => x !== id));
    } catch (e) {
      onError(e.message);
    }
  };

  const areas = useMemo(() => {
    const set = new Set();
    items.forEach((it) => Object.keys(it.scores || {}).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [items]);

  const radarData = useMemo(() => {
    if (selected.length === 0) return [];
    return areas.map((area) => {
      const row = { label: area === "Desenvolvimento Pessoal" ? "Desenv. Pessoal" : area };
      selected.forEach((id) => {
        const doc = items.find((x) => x.id === id);
        row[shortDate(doc?.created_at)] = Number(doc?.scores?.[area] || 0);
      });
      return row;
    });
  }, [selected, items, areas]);

  const compareLines = selected.map((id) => {
    const d = items.find((x) => x.id === id);
    return { id, label: shortDate(d?.created_at) };
  });

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B7280]" data-testid="coach-history-loading">
        <Loader2 size={16} className="animate-spin" /> A carregar histórico…
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 text-center" data-testid="coach-history-empty">
        <History size={28} className="mx-auto text-[#978D78] mb-2" />
        <p className="text-sm text-[#374151]">
          Ainda não fizeste nenhuma avaliação. Começa por criar uma nova para
          veres a tua Roda da Vida aparecer aqui.
        </p>
      </div>
    );
  }

  const COLORS = ["#4A5E2E", "#0F1829"];

  return (
    <div className="space-y-5" data-testid="coach-history">
      <RodaEvolucao items={items} />

      {selected.length >= 1 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[#0F1829] mb-2">
            Evolução comparativa {selected.length === 2 ? "(2 avaliações)" : ""}
          </h3>
          <div className="w-full h-[280px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius={isMobileWidth() ? "65%" : "80%"}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="label" tick={{ fill: "#0F1829", fontSize: isMobileWidth() ? 10 : 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#978D78", fontSize: 10 }} />
                {compareLines.map((c, i) => (
                  <Radar
                    key={c.id}
                    name={c.label}
                    dataKey={c.label}
                    stroke={COLORS[i]}
                    fill={COLORS[i]}
                    fillOpacity={0.25}
                  />
                ))}
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Mobile: cards | Desktop: tabela */}
      <div className="sm:hidden space-y-3">
        {items.map((it) => {
          const avg = averageScore(it.scores);
          const isSel = selected.includes(it.id);
          return (
            <div
              key={it.id}
              className={`bg-white rounded-2xl border p-4 shadow-sm ${isSel ? "border-[#4A5E2E]/50 bg-[#F3F4F6]/40" : "border-[#E5E7EB]"}`}
              data-testid={`coach-history-card-${it.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0F1829] font-medium">
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggle(it.id)}
                    className="accent-[#4A5E2E] w-4 h-4"
                  />
                  {fullDate(it.created_at)}
                </label>
                <span className="font-mono font-semibold text-sm" style={{ color: scoreColor(avg) }}>
                  {avg.toFixed(1)}/10
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1 flex-wrap">
                  <Badge on={!!it.insights} label="Insights" />
                  <Badge on={!!it.analise} label="Auto-análise" />
                </div>
                <div className="inline-flex gap-1 shrink-0">
                  <button
                    onClick={() => onPick(it.id)}
                    className="p-2 rounded-md border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#0F1829]"
                    title="Ver detalhes"
                    data-testid={`coach-history-view-m-${it.id}`}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    className="p-2 rounded-md border border-red-100 hover:bg-red-50 text-red-600"
                    title="Apagar"
                    data-testid={`coach-history-delete-m-${it.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden sm:block bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FBFAF7] text-[#6B7280] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Média</th>
              <th className="text-left px-4 py-3">Estado da IA</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const avg = averageScore(it.scores);
              const hasInsights = !!it.insights;
              const hasAnalise = !!it.analise;
              const isSel = selected.includes(it.id);
              return (
                <tr
                  key={it.id}
                  className={`border-t border-[#F3F4F6] ${isSel ? "bg-[#F3F4F6]/60" : ""}`}
                  data-testid={`coach-history-row-${it.id}`}
                >
                  <td className="px-4 py-3 text-[#0F1829]">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(it.id)}
                        className="accent-[#4A5E2E]"
                      />
                      {fullDate(it.created_at)}
                    </label>
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold" style={{ color: scoreColor(avg) }}>
                    {avg.toFixed(1)}/10
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      <Badge on={hasInsights} label="Insights" />
                      <Badge on={hasAnalise} label="Auto-análise" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => onPick(it.id)}
                        className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#0F1829]"
                        title="Ver detalhes"
                        data-testid={`coach-history-view-${it.id}`}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => remove(it.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        title="Apagar"
                        data-testid={`coach-history-delete-${it.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({ on, label }) {
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
        on ? "bg-[#4A5E2E]/15 text-[#4A5E2E]" : "bg-[#F3F4F6] text-[#9CA3AF]"
      }`}
    >
      {on ? "✓" : "○"} {label}
    </span>
  );
}

function averageScore(scores) {
  const vals = Object.values(scores || {}).map((v) => Number(v) || 0);
  if (!vals.length) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function scoreColor(v) {
  if (v <= 3) return "#B91C1C";
  if (v <= 6) return "#C2831A";
  return "#4A5E2E";
}

function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
}
function fullDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
